import type { SesVerdict } from "encamp-shared/src/emailIntake";

import { useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Box,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import CopyableTypography from "components/Typography/CopyableTypography";
import { gql } from "generated-graphql";
import { InboundEmailDetailQuery, EmailIntakeSenderMatchType } from "generated-graphql/graphql";
import { prettyPrintDateTimeMed } from "util/dates";

import {
  attachmentMetaLabel,
  attachmentsLabel,
  ATTACHMENT_CODE_LABELS,
  blockedActionLines,
  customerDocumentHref,
  customerManifestHref,
  documentFateLabel,
  missingAttachmentDetailText,
  missingReplyText,
  noDocumentsText,
  statusDisplay,
  REJECTION_HELP,
  historyLine,
  verdictDisplay,
} from "./displayText";
import { DETAIL_PANEL_INDENT_PX } from "./inboundGridColumns";

const DETAIL = gql(`
  query InboundEmailDetail($id: ID!) {
    receivedEmail(id: $id) {
      id
      receivedAt
      fromAddress
      envelopeSender
      recipients
      subject
      status
      rejectionReason
      attachmentsProcessedCount
      attachmentsTotalCount
      bodyText
      hasHtmlBody
      senderIp
      verdictSpam
      verdictVirus
      verdictSpf
      verdictDkim
      verdictDmarc
      sesMessageId
      attention
      actions {
        kind
        available
        blockedReason
      }
      competingSenders {
        senderId
        tenantId
        tenantName
        value
        matchType
      }
      lastReplayedAt
      lastReplayedBy
      dismissedAt
      dismissedBy
      rawEmailDownloadUrl
      matchedSender {
        id
        value
        label
      }
      tenant {
        id
        name
      }
      attachments {
        id
        filename
        accepted
        messageCode
        mimeType
        sizeBytes
        document {
          id
          title
          extractionStatus
          needsReview
          reviewedAt
          suggestedFacility {
            id
            name
          }
          manifestFacility {
            id
            name
          }
        }
      }
      reply {
        id
        to
        sentAt
      }
      documents {
        id
        title
        documentType
        createdAt
        extractionStatus
        needsReview
        reviewedAt
        suggestedFacility {
          id
          name
        }
        manifestFacility {
          id
          name
        }
        processedManifest {
          id
          manifestTrackingNumber
        }
      }
    }
  }
`);

/**
 * A link into the customer's app, or plain text when there is no customer app to point
 * at: an email that resolved to no organization gets the label and no dead link.
 */
function CustomerAppLink({ href, label }: { href: string | null; label: string }) {
  if (!href) {
    return <>{label}</>;
  }
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
    >
      {label}
      <OpenInNew fontSize="inherit" />
    </MuiLink>
  );
}

function Verdict({ verdict, value }: { verdict: SesVerdict; value: string }) {
  const { color, label, help } = verdictDisplay(verdict, value);
  const chip = <StatusChip color={color} label={label} sx={{ maxWidth: "100%" }} />;
  // StatusChip is not forwardRef, so Tooltip needs an element to attach to.
  return help ? (
    <Tooltip title={help}>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {/* A div, not the default p: the value slot takes any node, and several fields
          pass an element that renders a block of its own. Nesting one inside a p is
          invalid HTML and React reports it as a hydration error. */}
      <Typography component="div" variant="body2" sx={{ wordBreak: "break-word" }}>
        {children}
      </Typography>
    </Stack>
  );
}

type InboundEmail = NonNullable<InboundEmailDetailQuery["receivedEmail"]>;

/**
 * Why Route or Replay is closed, in the API's words, right under the refusal's own
 * explanation so the cause and its consequence read together. The row menu lists
 * the blocked actions disabled and points here.
 */
function BlockedActionsField({ email }: { email: InboundEmail }) {
  const lines = blockedActionLines(email);
  if (lines.length === 0) return null;
  return (
    <Field label="Why actions are unavailable">
      <Stack spacing={0.5}>
        {lines.map((line) => (
          <Typography key={line.key} variant="body2">
            <Typography component="span" variant="body2" fontWeight="medium">
              {line.label}:
            </Typography>{" "}
            {line.reason}
          </Typography>
        ))}
      </Stack>
    </Field>
  );
}

const FATE_TONE_COLOR = {
  neutral: "text.secondary",
  warning: "warning.main",
  error: "error.main",
} as const;

/**
 * What became of a filed document after filing — parsed or not, reviewed or waiting,
 * and which facility. The part of the trail this page could not show before: three
 * emailed manifests once sat "Filed" here while every one of their documents had
 * failed to parse, and nothing on the row said so.
 */
function DocumentFateCaption({ document }: { document: Parameters<typeof documentFateLabel>[0] }) {
  const { text, tone } = documentFateLabel(document);
  return (
    <Typography variant="caption" color={FATE_TONE_COLOR[tone]}>
      · {text}
    </Typography>
  );
}

/**
 * Filed, refused, or only looked at. The third is mail waiting on a person — held,
 * whether from an Encamp sender or an ambiguous one — whose attachments were listed
 * but never opened; a red
 * cross there would say "failed" about a file nobody has tried to file yet.
 */
function AttachmentMark({
  attachment,
}: {
  attachment: { accepted: boolean; messageCode?: string | null; document?: unknown };
}) {
  if (attachment.accepted) {
    return (
      <Typography variant="body2" color="success.main">
        ✓
      </Typography>
    );
  }
  const onlyPeeked = !attachment.messageCode && !attachment.document;
  return (
    <Typography variant="body2" color={onlyPeeked ? "text.secondary" : "error.main"}>
      {onlyPeeked ? "○" : "✕"}
    </Typography>
  );
}

/**
 * Per-attachment outcomes: what filed, what was refused, and why.
 *
 * The reason this panel exists. These outcomes used to be computed, sent to the
 * sender, and discarded — so the sender was told "manifest2.pdf is not a PDF" while
 * staff could see only "2 of 5".
 */
function AttachmentList({ email }: { email: InboundEmail }) {
  return (
    <>
      {email.attachments.length === 0 &&
        missingAttachmentDetailText(email.attachmentsTotalCount) && (
          <Typography variant="body2" color="text.secondary">
            {missingAttachmentDetailText(email.attachmentsTotalCount)}
          </Typography>
        )}

      {email.attachments.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="overline" color="text.secondary">
            Attachments
          </Typography>
          {email.attachments.map((attachment) => (
            <Stack
              key={attachment.id}
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
              useFlexGap
            >
              <AttachmentMark attachment={attachment} />
              <Typography variant="body2">
                {attachment.document ? (
                  <CustomerAppLink
                    href={customerDocumentHref(email.tenant?.id, attachment.document.id)}
                    label={attachment.filename ?? "(no filename)"}
                  />
                ) : (
                  (attachment.filename ?? "(no filename)")
                )}
              </Typography>
              {attachment.messageCode && (
                <Typography variant="body2" color="text.secondary">
                  {ATTACHMENT_CODE_LABELS[attachment.messageCode]}
                </Typography>
              )}
              {attachmentMetaLabel(attachment) && (
                <Typography variant="caption" color="text.secondary">
                  {attachmentMetaLabel(attachment)}
                </Typography>
              )}
              {attachment.document && <DocumentFateCaption document={attachment.document} />}
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );
}

/** The body of the message that arrived, when it came as text. */
function MessageBody({
  bodyText,
  hasHtmlBody,
}: {
  bodyText?: string | null;
  hasHtmlBody: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Message body
      </Typography>
      {bodyText ? (
        <Typography
          variant="body2"
          // Plain text in a pre-wrap block: the API deliberately never returns the
          // HTML body, so there is nothing here to render as markup.
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {bodyText}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {hasHtmlBody
            ? "This message had an HTML body only, which is not shown."
            : "No message body."}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * What we kept about one inbound email, for a staff member working out what happened
 * to it — until this existed, the only record of why a message was refused was in
 * CloudWatch and in the reply the sender got.
 *
 * The body renders as plain text because that is all the API returns. The message is
 * written by whoever sent it and the intake address accepts mail from anyone, so
 * rendering its HTML here would make every inbound message a stored-XSS attempt.
 */
export function InboundEmailDetail({ id }: { id: string }) {
  const { data, loading, error } = useQuery(DETAIL, { variables: { id } });
  const email = data?.receivedEmail;

  if (loading && !email) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error || !email) {
    return (
      <Box sx={{ py: 2, pr: 2, pl: `${DETAIL_PANEL_INDENT_PX}px` }}>
        <Typography variant="body2" color="text.secondary">
          {error ? `Couldn't load this email. ${error.message}` : "This email is no longer stored."}
        </Typography>
      </Box>
    );
  }

  const status = statusDisplay(email);
  const reasonHelp = email.rejectionReason ? REJECTION_HELP[email.rejectionReason] : null;

  return (
    // Indented past the chevron column so the panel's content starts where the row's
    // own data does. Flush against the grid's left edge, it read as a separate block
    // that happened to appear rather than as this row opening up.
    <Box
      sx={(theme) => ({
        py: 2,
        pr: 2,
        pl: `${DETAIL_PANEL_INDENT_PX}px`,
        bgcolor: "action.hover",
        height: "100%",
        overflow: "auto",
        // A drawn scrollbar rather than the platform's overlay one, which on macOS
        // fades out at rest and left no sign that this panel scrolls at all.
        //
        // Two things have to be true together. Declaring a `::-webkit-scrollbar` width
        // is what makes Chrome and Safari reserve a permanent gutter instead of
        // overlaying — but it also opts out of drawing the native thumb, so a colour
        // here is not decoration, it is the only thing rendered. Omit it and the result
        // is an 8px empty gutter, which looks exactly like no scrollbar at all.
        //
        // Real colour strings off the theme, not `sx` palette tokens: `background`
        // is not palette-aware (only `backgroundColor`/`bgcolor` are) and a token
        // there is dropped silently, which is a quiet way to think you have styled
        // something you have not.
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.action.active,
          borderRadius: "4px",
          "&:hover": { backgroundColor: theme.palette.text.primary },
        },
        // Firefox has no thumb pseudo-element. Scoped, because setting
        // `scrollbar-width` unconditionally makes Chrome ignore the rules above and
        // go back to the overlay bar.
        "@supports not selector(::-webkit-scrollbar)": {
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.action.active} transparent`,
        },
      })}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <StatusChip color={status.color} label={status.label} />
          <Typography variant="subtitle2">{email.subject || "(no subject)"}</Typography>
        </Stack>

        {reasonHelp && (
          <Typography variant="body2" color="text.secondary">
            {reasonHelp}
          </Typography>
        )}

        <BlockedActionsField email={email} />

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          <Field label="From (header)">{email.fromAddress ?? "—"}</Field>
          {/* Shown because they legitimately differ, and resolution matches on the
              header — conflating them is what sends someone to allowlist a bounce
              address that can never match. */}
          <Field label="Envelope sender">{email.envelopeSender}</Field>
          <Field label="Sent to">{email.recipients.join(", ") || "—"}</Field>
          <Field label="Received">{prettyPrintDateTimeMed(email.receivedAt)}</Field>
          <Field label="Attachments">{attachmentsLabel(email)}</Field>
          <Field label="Matched allowlist entry">
            {email.matchedSender
              ? (email.matchedSender.label ?? email.matchedSender.value)
              : "None — nothing accepted it"}
          </Field>
          {/* Which organizations are actually fighting over this sender. Without them
              the row says an allowlist conflict exists and leaves the reader to find
              it, which means leaving the page — and finding them is the whole of the
              fix, since removing the wrong one and replaying is the good resolution. */}
          {email.competingSenders.length > 0 && (
            <Field label="Claimed by">
              <Stack spacing={0.25}>
                {email.competingSenders.map((claim) => (
                  <Typography key={claim.senderId} variant="body2">
                    {claim.tenantName}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {" · "}
                      {claim.matchType === EmailIntakeSenderMatchType.Domain
                        ? `domain ${claim.value}`
                        : claim.value}
                    </Typography>
                  </Typography>
                ))}
              </Stack>
            </Field>
          )}
          <Field label="Organization">
            {email.tenant?.name ?? "Unattributed — no organization matched"}
          </Field>
          {/* Sent, and to whom. Never "delivered": nothing records a bounce, so
              claiming arrival would assert something we do not know. */}
          <Field label="Reply sent">
            {email.reply
              ? `${email.reply.to} · ${prettyPrintDateTimeMed(email.reply.sentAt)}`
              : missingReplyText(email.status, Boolean(email.tenant))}
          </Field>
          <Field label="Sender IP">{email.senderIp}</Field>
          {/* Click to copy, rather than a link. The raw message is in S3 and the
              lambda's logs are in CloudWatch — both bucket and log group are
              environment-specific and reachable only by an engineer, so building
              console URLs into the page would age badly and mostly 403. Handing over
              the id is the useful part. */}
          <Field label="SES message id">
            <CopyableTypography
              text={email.sesMessageId}
              variant="body2"
              sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
            />
          </Field>
          {/* The message as SES stored it, for judging a refusal before acting on it.
              Signed for minutes, not hours, and only offered when the object is
              actually there — the rows most likely to be opened here are old refusals
              whose raw message expired under the previous retention rule. */}
          <Field label="Raw message">
            {email.rawEmailDownloadUrl ? (
              <MuiLink
                href={email.rawEmailDownloadUrl}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                Download .eml
                <Download fontSize="inherit" />
              </MuiLink>
            ) : (
              "No longer in storage"
            )}
          </Field>
          {historyLine(email) && <Field label="Last action">{historyLine(email)}</Field>}
          {email.dismissedAt && (
            <Field label="Dismissed">
              {email.dismissedBy ?? "—"} · {prettyPrintDateTimeMed(email.dismissedAt)}
              <Typography component="div" variant="caption" color="text.secondary">
                Marked as needing no action. Restore it from the row&apos;s actions if that changes.
              </Typography>
            </Field>
          )}
        </Box>

        <Divider />

        <AttachmentList email={email} />

        <Divider />

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            SES checks
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Verdict verdict="spam" value={email.verdictSpam} />
            <Verdict verdict="virus" value={email.verdictVirus} />
            <Verdict verdict="spf" value={email.verdictSpf} />
            <Verdict verdict="dkim" value={email.verdictDkim} />
            <Verdict verdict="dmarc" value={email.verdictDmarc} />
          </Stack>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Documents created ({email.documents.length})
          </Typography>
          {email.documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None. {noDocumentsText(email)}
            </Typography>
          ) : (
            email.documents.map((doc) => (
              <Typography key={doc.id} variant="body2">
                <CustomerAppLink
                  href={customerDocumentHref(email.tenant?.id, doc.id)}
                  label={doc.title ?? "(untitled)"}
                />
                <Typography component="span" variant="caption" color="text.secondary">
                  {" · "}
                  {doc.documentType}
                </Typography>{" "}
                <DocumentFateCaption document={doc} />
                {/* Two links on purpose: the document is always there, the manifest only
                    once the parse was accepted into the waste data. A row with the first
                    and not the second is a manifest that has not made it that far. */}
                {doc.processedManifest && (
                  <>
                    {" · "}
                    <CustomerAppLink
                      href={customerManifestHref(email.tenant?.id, doc.processedManifest.id)}
                      label={`Manifest ${doc.processedManifest.manifestTrackingNumber}`}
                    />
                  </>
                )}
              </Typography>
            ))
          )}
        </Stack>

        <Divider />

        <MessageBody bodyText={email.bodyText} hasHtmlBody={email.hasHtmlBody} />
      </Stack>
    </Box>
  );
}
